import { Field, type FieldProps } from '@upright/ui/common/generic/components/field';
import TaillessLineArrowDown1 from '@upright/ui/icons/img/streamline/tailless-line-arrow-down-1.svg';
import TaillessLineArrowUp1 from '@upright/ui/icons/img/streamline/tailless-line-arrow-up-1.svg';
import { ComboBox as AriaComboBox, ComboBoxProps, ComboBoxValue } from 'react-aria-components';
import { useRef } from 'react';
import { ListBox, type Props as ListBoxProps } from '../ListBox';
import { InputGroup, type InputGroupProps } from '../../input';
import { IconButton } from '../../button';
import { Box, Stack } from '../../layout';
import { ClearTextSlots, Text } from '../../typography';

export type Props<T extends { value: string; label?: string }, M extends 'single' | 'multiple'> = Pick<
  ComboBoxProps<T, M>,
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'isDisabled'
  | 'onOpenChange'
  | 'selectionMode'
  /*
   * The four below are what a search needs and a value picker does not: the text the user typed, as
   * state the caller owns. A picker filters a list it already holds, so react-aria's own input state
   * is enough. A search has to send that text somewhere and render what comes back, which means
   * reading it (`inputValue` / `onInputChange`), opening on the first keystroke rather than on focus
   * (`menuTrigger`), and being allowed to open with an empty list so there is somewhere to say
   * "searching" (`allowsEmptyCollection`, paired with `ListBox`'s `renderEmptyState`).
   */
  | 'inputValue'
  | 'onInputChange'
  | 'menuTrigger'
  | 'allowsEmptyCollection'
> &
  ListBoxProps<T> &
  FieldProps &
  Pick<InputGroupProps, 'placeholder' | 'maxWidth' | 'startAdornment'> & {
    shouldMatchTriggerWidth?: boolean;
    /**
     * Whether the field ends in a chevron that opens the list. On by default, because a picker whose
     * list is closed has to advertise that there is one. A search does not: its list is a response to
     * typing, its affordance is the placeholder and its leading magnifier, and a chevron next to that
     * magnifier would read as a second, unrelated action.
     */
    hasToggleButton?: boolean;
    /**
     * Whether `items` are already the answer to what was typed, so react-aria must not narrow them
     * again. A picker's `items` are the whole list and react-aria filters them with its own
     * case- and diacritic-insensitive `contains`, which is exactly right. A search's `items` are what
     * the server sent back, and filtering those a second time silently drops every hit matched on
     * anything other than a literal substring of the raw input — a preprocessed query, an identifier,
     * two words in the other order — leaving a list that looks like the backend found nothing.
     *
     * The mechanism, because it is not guessable from the outside: this hands `items` to react-aria's
     * own `ComboBox` as well as to the list, and `useComboBoxState` skips its filter pass whenever the
     * combobox itself was given `items` ("If props.items isn't provided, rely on collection
     * filtering", react-aria-components/ComboBox). Passing them only to the list, as the picker shape
     * does, is what keeps that filtering on.
     */
    isFilteredByCaller?: boolean;
  };

/**
 * A text field with a list under it. Two shapes, one component:
 *
 * - **value picker** (the default) — `items` are all the options, react-aria filters them as the user
 *   types, the list opens on focus and a chevron says so. `portfolio-type-select` is the small case.
 * - **search** — the caller owns the typed text (`inputValue` / `onInputChange`), fetches with it, and
 *   passes the results back as `items`. Add `menuTrigger="input"`, `allowsEmptyCollection`,
 *   `renderEmptyState`, `hasToggleButton={false}` and `isFilteredByCaller`;
 *   `unified-shell-search` is the reference.
 *
 * Both get react-aria's keyboard contract — arrow keys, typeahead, escape-to-close,
 * `aria-activedescendant` — which is the reason to reach for this rather than an input beside a list.
 *
 * One rule the types cannot state: when `children` is a function, it must return an `Option` carrying
 * an `id`, directly. react-aria takes each row's key from the returned element's own `id` prop, so
 * returning a row component of your own instead throws "Could not determine key for item" and unmounts
 * the tree — a blank app, from a message that names neither the list nor the row. A row component goes
 * inside the `Option`, not around it.
 */
export function ComboBox<T extends { value: string; label?: string }, M extends 'single' | 'multiple' = 'single'>({
  children,
  value,
  defaultValue,
  onChange,
  isDisabled,
  onOpenChange,
  selectionMode,
  label,
  isLabelVisuallyHidden,
  description,
  error,
  placeholder,
  maxWidth,
  placement,
  shouldMatchTriggerWidth,
  popoverMinInlineSize,
  items,
  inputValue,
  onInputChange,
  menuTrigger = 'focus',
  allowsEmptyCollection,
  renderEmptyState,
  startAdornment,
  hasToggleButton = true,
  isFilteredByCaller,
}: Props<T, M>) {
  /*
   * The box the list positions and sizes itself against. Left to react-aria it would be the `input`,
   * which is the field minus its adornments — see `ListBox`'s `triggerRef`.
   */
  const fieldRef = useRef<HTMLDivElement>(null);

  return (
    <AriaComboBox
      value={value}
      defaultValue={defaultValue}
      inputValue={inputValue}
      onInputChange={onInputChange}
      onOpenChange={onOpenChange}
      onChange={onChange}
      isDisabled={isDisabled}
      selectionMode={selectionMode}
      allowsEmptyCollection={allowsEmptyCollection}
      menuTrigger={menuTrigger}
      // Presence, not value, is the signal react-stately reads — see `isFilteredByCaller`.
      items={isFilteredByCaller ? items : undefined}
    >
      {({ isOpen }) => (
        <Field label={label} isLabelVisuallyHidden={isLabelVisuallyHidden} description={description} error={error}>
          <InputGroup
            groupRef={fieldRef}
            placeholder={placeholder}
            maxWidth={maxWidth}
            startAdornment={startAdornment}
            endAdornment={
              hasToggleButton ? (
                <IconButton
                  size="small"
                  variant="outlined"
                  aria-label={isOpen ? 'close combobox' : 'open combobox'}
                  icon={isOpen ? TaillessLineArrowUp1 : TaillessLineArrowDown1}
                />
              ) : undefined
            }
          />
          {selectionMode === 'multiple' && (
            <ComboBoxValue<T>>
              {({ selectedItems }) => {
                return (
                  <Stack row wrap gap="xs">
                    {selectedItems
                      .filter(item => item !== null)
                      .map(item => (
                        <Box
                          display="flex"
                          key={item.value}
                          paddingInline="sm"
                          paddingBlock="sm"
                          borderRadius="sm"
                          backgroundColor="gray-100"
                        >
                          <ClearTextSlots>
                            <Text size="md" height="none">
                              {item.label ?? item.value}
                            </Text>
                          </ClearTextSlots>
                        </Box>
                      ))}
                  </Stack>
                );
              }}
            </ComboBoxValue>
          )}
          <ListBox
            placement={placement}
            triggerRef={fieldRef}
            shouldMatchTriggerWidth={shouldMatchTriggerWidth}
            popoverMinInlineSize={popoverMinInlineSize}
            items={items}
            renderEmptyState={renderEmptyState}
          >
            {children}
          </ListBox>
        </Field>
      )}
    </AriaComboBox>
  );
}
